import { SearchOutlined } from "@ant-design/icons";
import { Table, Input, Col, Row, Modal, Button, Spin, Space, Popconfirm, message } from "antd";
import React, { useState } from "react";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../../../firebase/config";

const { Search } = Input;

export default function OrderPlateSelect({ selectedPlate, setSelectedPlate, plateData, loading, setTechnicalPage }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    client: "",
    product: "",
    layoutName: "",
    rows: "",
  });
  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = (value) => {
    setSelectedPlate(value);
    setTechnicalPage(value.technicalPage);
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleDeletePlate = async (record) => {
    try {
      await deleteDoc(doc(db, "vivaPlateList", record.id));
      message.success("Plate deleted successfully!");
    } catch (error) {
      console.error("Error deleting plate:", error);
      message.error("Failed to delete plate");
    }
  };

  //default columns for table
  const defaultColumns = [
    {
      title: "Client",
      dataIndex: "client",
      align: "left",
    },
    {
      title: "Product",
      dataIndex: "product",
      align: "left",
    },
    {
      title: "Code",
      dataIndex: "code",
      align: "left",
    },
    {
      title: "Rows",
      dataIndex: "rows",
      align: "left",
      width: "8%",
    },
    {
      title: "Width",
      dataIndex: "width",
      align: "left",
      width: "8%",
      sorter: {
        compare: (a, b) => a.width - b.width,
        multiple: 2,
      },
    },
    {
      title: "Height",
      dataIndex: "height",
      align: "left",
      width: "8%",
      sorter: {
        compare: (a, b) => a.height - b.height,
        multiple: 2,
      },
    },
    {
      title: "Layout Name",
      dataIndex: "layoutDesign",
      align: "left",
    },
    {
      title: "Select",
      align: "left",
      width: "8%",
      render: (value) => (
        <Button type="primary" size="small" onClick={() => handleOk(value)}>
          Select
        </Button>
      ),
    },
    {
      title: "Delete",
      align: "left",
      width: "8%",
      render: (value, record) => (
        <Popconfirm
          title="Delete Plate"
          description="Are you sure you want to delete this plate?"
          onConfirm={() => handleDeletePlate(record)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}>
          <Button type="primary" danger size="small">
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // filter function with specific field filters
  const filterData = (data) => {
    if (!data) return [];
    return data.filter((row) => {
      const matchClient = !filters.client || (row.client && row.client.toLowerCase().includes(filters.client.toLowerCase()));
      const matchProduct = !filters.product || (row.product && row.product.toLowerCase().includes(filters.product.toLowerCase()));
      const matchLayout = !filters.layoutName || (row.layoutDesign && row.layoutDesign.toLowerCase().includes(filters.layoutName.toLowerCase()));
      const matchRows = !filters.rows || (row.rows && String(row.rows).includes(filters.rows));
      const matchSearch = !searchText || Object.values(row).some((value) => {
        if (typeof value === "string") return value.toLowerCase().includes(searchText.toLowerCase());
        if (typeof value === "number") return String(value).includes(searchText);
        return false;
      });

      return matchClient && matchProduct && matchLayout && matchRows && matchSearch;
    });
  };

  if (loading) {
    return <Spin />;
  }

  const filteredData = filterData(plateData);
  return (
    <>
      <SearchOutlined onClick={showModal} disabled={loading} />
      <Modal
        title="Search Plates"
        width="90vw"
        style={{ maxWidth: "1800px" }}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        bodyStyle={{ maxHeight: "85vh", overflowY: "auto" }}>
        <Row gutter={[10, 10]}>
          <Col span={24}>
            <strong>Currently Selected: {selectedPlate && selectedPlate.code}</strong>
          </Col>

          {/* Filter Section */}
          <Col span={24}>
            <Space wrap style={{ width: "100%" }}>
              <Input
                placeholder="Filter by Client"
                value={filters.client}
                onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                style={{ width: 200 }}
              />
              <Input
                placeholder="Filter by Product"
                value={filters.product}
                onChange={(e) => setFilters({ ...filters, product: e.target.value })}
                style={{ width: 200 }}
              />
              <Input
                placeholder="Filter by Layout Name"
                value={filters.layoutName}
                onChange={(e) => setFilters({ ...filters, layoutName: e.target.value })}
                style={{ width: 200 }}
              />
              <Input
                placeholder="Filter by Rows"
                value={filters.rows}
                onChange={(e) => setFilters({ ...filters, rows: e.target.value })}
                style={{ width: 120 }}
              />
              <Button
                onClick={() => setFilters({ client: "", product: "", layoutName: "", rows: "" })}
              >
                Clear Filters
              </Button>
            </Space>
          </Col>

          {/* Search Bar */}
          <Col span={24}>
            <Search
              disabled={loading}
              placeholder="Global search (searches all fields)"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ marginBottom: 16 }}
            />
          </Col>

          {/* Table */}
          <Col span={24}>
            <Table
              loading={loading}
              size="small"
              columns={defaultColumns}
              rowKey={(record) => record.id}
              dataSource={filteredData && filteredData}
              pagination={{ pageSize: 100, showSizeChanger: true, pageSizeOptions: [50, 100, 200, 500] }}
              scroll={{ y: "calc(85vh - 300px)" }}
            />
          </Col>
        </Row>
      </Modal>
    </>
  );
}
